/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMemo,
  useCallback,
  useState,
  useEffect,
  ReactElement,
  JSXElementConstructor,
  ReactFragment,
  ReactPortal,
} from 'react';
import { Marker } from 'react-map-gl';
import { theme } from 'twin.macro';
import Supercluster from 'supercluster';
import { atom, useAtomValue } from 'jotai';
import { Source, Layer } from 'react-map-gl';
import * as turf from '@turf/turf';

import { JobLocationCoordinates } from '@restworld/data-services';

import { explorerRestaurantMapMarkersAtom } from '../../job-explorer-restaurant-explorer.atoms';
import {
  getPopupLocationCoordinatesAtom,
  getMapRefAtom,
  jobLocationCoordinatesAtom,
} from '../../job-explorer-map.atoms';
import MarkerWithFloatingPopup from '../job-explorer-map.components/marker-with-floating-popup';
import { isRestaurantMarkerAtom } from '../popup-info-el';

export type JobLocationCoordinatesClusterType = {
  properties: {
    cluster: any;
    point_count:
      | string
      | number
      | boolean
      | ReactElement<any, string | JSXElementConstructor<any>>
      | ReactFragment
      | ReactPortal;
  };
  extra?: JobLocationCoordinates;
  id?: string | number;
  geometry: { coordinates: number[] };
};

export const clusterRadiusAtom = atom(40);
export const clusterMaxZoomAtom = atom(14);
export const clusterMinPointsAtom = atom(2);

const useMarkers = ({ explorerType }: { explorerType?: 'restaurants' | 'job-positions' }) => {
  const jobCoordinates = useAtomValue(jobLocationCoordinatesAtom);
  const explorerRestaurantMapMarkers = useAtomValue(explorerRestaurantMapMarkersAtom);

  const processMarkers = useCallback((markers: JobLocationCoordinates[], isRestaurantMarker: boolean) => {
    const offsetDistance = 0.00001;
    const seenCoordinates = new Set<string>();
    return (markers || []).reduce((acc, marker) => {
      if (marker.coordinates.lat !== null && marker.coordinates.lon !== null) {
        let { lat, lon } = marker.coordinates;
        let key = `${lat},${lon}`;
        while (seenCoordinates.has(key)) {
          lat += offsetDistance;
          lon += offsetDistance;
          key = `${lat},${lon}`;
        }
        seenCoordinates.add(key);
        return [
          ...acc,
          {
            ...marker,
            coordinates: { ...marker.coordinates, lat, lon },
            ...(isRestaurantMarker ? { is_restaurant_marker: true } : { is_job_position_marker: true }),
          },
        ];
      }
      return acc;
    }, [] as JobLocationCoordinates[]);
  }, []);

  return useMemo(
    () =>
      explorerType === 'restaurants'
        ? processMarkers(explorerRestaurantMapMarkers, true)
        : explorerType === 'job-positions'
        ? processMarkers(jobCoordinates, false)
        : jobCoordinates?.length &&
          explorerRestaurantMapMarkers?.length && [
            ...processMarkers(jobCoordinates, false),
            ...processMarkers(explorerRestaurantMapMarkers, true),
          ],
    [explorerRestaurantMapMarkers, explorerType, jobCoordinates, processMarkers]
  );
};

const useBufferedArea = () => {
  const popupLocationCoordinates = useAtomValue(getPopupLocationCoordinatesAtom);

  return useMemo(() => {
    const defaultKMRadius = 1;
    if (!popupLocationCoordinates?.coordinates?.is_approximate || !popupLocationCoordinates?.coordinates?.lon)
      return null;

    try {
      return turf.buffer(
        turf.point([popupLocationCoordinates.coordinates.lon, popupLocationCoordinates.coordinates.lat]),
        defaultKMRadius,
        { units: 'kilometers' }
      );
    } catch (error) {
      console.error('Error creating circular buffer:', error);
      return null;
    }
  }, [popupLocationCoordinates]);
};

const useSupercluster = (explorerType?: 'restaurants' | 'job-positions') => {
  const markers = useMarkers({ explorerType });
  const clusterRadius = useAtomValue(clusterRadiusAtom);
  const clusterMaxZoom = useAtomValue(clusterMaxZoomAtom);
  const clusterMinPoints = useAtomValue(clusterMinPointsAtom);

  const superCluster = useMemo(
    () =>
      markers?.length
        ? new Supercluster({
            radius: clusterRadius,
            maxZoom: clusterMaxZoom,
            minPoints: clusterMinPoints,
          }).load(
            markers.map(({ coordinates, ...rest }) => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [coordinates.lon, coordinates.lat],
              },
              properties: null,
              extra: { ...rest },
            }))
          )
        : null,
    [clusterRadius, clusterMaxZoom, clusterMinPoints, markers]
  );

  return { superCluster };
};

const useClusters = (explorerType?: 'restaurants' | 'job-positions') => {
  const [clusterData, setClusterData] = useState<any[]>([]);

  const mapRef = useAtomValue(getMapRefAtom);
  const popupLocationCoordinates = useAtomValue(getPopupLocationCoordinatesAtom);

  const { superCluster } = useSupercluster(explorerType);

  const handleClusters = useCallback(() => {
    const bounds = mapRef.current?.getBounds();
    const zoom = mapRef.current?.getZoom();

    if (bounds) {
      try {
        const clusters = superCluster?.getClusters(
          [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
          Math.floor(zoom)
        );
        setClusterData(clusters);
      } catch (error) {
        console.error('Error clustering:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superCluster]);

  useEffect(() => {
    handleClusters();
  }, [handleClusters]);

  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    const debouncedHandleClusters = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        handleClusters();
      }, 400);
    };

    mapRef.current?.on('moveend', debouncedHandleClusters);

    return () => {
      clearTimeout(debounceTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClusters]);

  const clusteredMarkers = useMemo(() => {
    return clusterData?.map((cluster: JobLocationCoordinatesClusterType) => {
      const { id: customClusterId, is_restaurant_marker: isRestaurantMarker } = cluster.extra || {};
      const clusterId = cluster?.properties?.['cluster_id'];
      const isCluster = cluster?.properties?.['cluster'];
      if (isCluster && clusterId) {
        try {
          const clusterCoordinates = superCluster?.getLeaves(parseInt(clusterId), Infinity);
          return (clusterCoordinates || [])?.map((leaf) => (
            <Marker
              key={`marker-${leaf?.['extra'].id}`}
              longitude={leaf.geometry.coordinates[0]}
              latitude={leaf.geometry.coordinates[1]}
              onClick={(e) => {
                const expansionZoom = Math.min(superCluster?.getClusterExpansionZoom(parseInt(clusterId)), 20);
                mapRef.current?.flyTo({
                  center: e.target.getLngLat(),
                  zoom: expansionZoom,
                  essential: true,
                });
              }}
            >
              <div
                className={
                  leaf?.['extra'].is_job_position_marker
                    ? 'h-4 w-4 bg-purple-700 opacity-90 rounded-full shadow-[0px_0px_8px_0px_rgba(0,0,26,0.25)] border border-neutral-10'
                    : 'h-4 w-4 bg-neutral-10 opacity-90 rounded-full shadow-[0px_0px_8px_0px_rgba(0,0,26,0.25)] border border-neutral-90'
                }
              />
            </Marker>
          ));
        } catch (error) {
          return null;
        }
      }

      return (
        <MarkerWithFloatingPopup
          key={`marker-${customClusterId}`}
          isOpen={popupLocationCoordinates?.id === customClusterId}
          cluster={{ ...cluster }}
          isRestaurantMarker={isRestaurantMarker}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterData, popupLocationCoordinates?.id, superCluster]);
  return clusteredMarkers;
};

export const useJobPositionClusters = () => {
  const clusteredMarkers = useClusters('job-positions');
  const bufferedArea = useBufferedArea();

  return (
    <>
      {clusteredMarkers}
      <Source id="circular-radius" type="geojson" data={bufferedArea}>
        <Layer
          id="point-90-hi"
          type="fill"
          paint={{
            'fill-color': theme`colors.primary.700`,
            'fill-opacity': 0.15,
          }}
        />
      </Source>
    </>
  );
};

export const useRestaurantClusters = () => {
  const clusteredMarkers = useClusters('restaurants');

  return <>{clusteredMarkers}</>;
};

export const useCombinedClusters = () => {
  const clusteredMarkers = useClusters();
  const bufferedArea = useBufferedArea();
  const isRestaurantMarker = useAtomValue(isRestaurantMarkerAtom);
  return (
    <>
      {clusteredMarkers}
      {!isRestaurantMarker && (
        <Source id="circular-radius" type="geojson" data={bufferedArea}>
          <Layer
            id="point-90-hi"
            type="fill"
            paint={{
              'fill-color': theme`colors.primary.700`,
              'fill-opacity': 0.15,
            }}
          />
        </Source>
      )}
    </>
  );
};
